/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '@ion/applications/src/helpers';
import {importConfirm} from '@ion/applications/src/store/actions/importer';
import {
  AuditChange,
  Entity,
  GenericObject,
  ManufacturedDevice
} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {AuditRecordType} from '@ion/dls/src/editors/audit/AuditGridUtils';
import {PageState} from '@/types/pages';
import {AdminAction, PageInitializeAction} from '@/types/redux';
import {gridDefaultPageSize} from '../../../utils/helpers';

// name of reducer
export const REDUCER_NAME                          = 'manufacturedDevices';

export const INITIALIZE_MANUFACTURED_DEVICES       = 'INITIALIZE_MANUFACTURED_DEVICES';
export const SET_GRID_COLUMNS                      = 'SET_MANUFACTURED_DEVICES_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS                   = 'UPDATE_MANUFACTURED_DEVICES_GRID_COLUMNS';
export const MANUFACTURED_DEVICES_GRID_REFRESHED   = 'MANUFACTURED_DEVICES_GRID_REFRESHED';
export const SET_GRID_PAGE_SIZE                    = 'SET_MANUFACTURED_DEVICES_GRID_PAGE_SIZE';

// search queries
export const UPDATE_MANUFACTURED_DEVICES_SEARCH_QUERIES = 'UPDATE_MANUFACTURED_DEVICES_SEARCH_QUERIES';
export const RESET_MANUFACTURED_DEVICES_SEARCH_QUERIES  = 'RESET_MANUFACTURED_DEVICES_SEARCH_QUERIES';

export const IMPORT_MANUFACTURED_DEVICES      = 'IMPORT_MANUFACTURED_DEVICES';

//manufactured devices audit
export const AUDIT_MANUFACTURED_DEVICES                     = 'AUDIT_MANUFACTURED_DEVICES';
export const AUDIT_MANUFACTURED_DEVICES_REQUEST             = 'AUDIT_MANUFACTURED_DEVICES_REQUEST';
export const AUDIT_MANUFACTURED_DEVICES_RESPONSE            = 'AUDIT_MANUFACTURED_DEVICES_RESPONSE';
export const AUDIT_MANUFACTURED_DEVICES_ERROR               = 'AUDIT_MANUFACTURED_DEVICES_ERROR';
export const AUDIT_MANUFACTURED_DEVICES_SHOW_CHANGES_DIALOG = 'AUDIT_MANUFACTURED_DEVICES_SHOW_CHANGES_DIALOG';

/**
 * initialize ManufacturedDevices
 */
export const initializeManufacturedDevices = (): PageInitializeAction => {
  return {type: INITIALIZE_MANUFACTURED_DEVICES};
};

/**
 * Invoked when Grid Refreshed
 */
export const manufacturedDevicesGridRefreshed = (): PageInitializeAction => {
  return {type: MANUFACTURED_DEVICES_GRID_REFRESHED};
};

/**
 * Invoked whenever user change the row per page dropdown
 */
export const setManufacturedDevicesGridPageSize = (pageSize: number) => ({
  type : SET_GRID_PAGE_SIZE, pageSize
});

// Updates the modular search queries for Manufactured Devices
export const updateManufacturedDevicesSearchQueries = (searchParams: ManufacturedDevice) => ({
  type : UPDATE_MANUFACTURED_DEVICES_SEARCH_QUERIES, searchParams
});

// Resets the Manufactured Devices filters state
export const resetManufacturedDevicesSearchQueries = () => ({
  type : RESET_MANUFACTURED_DEVICES_SEARCH_QUERIES
});

export const importManufacturingDevices = (show = true, result = null) => {
  return async(dispatch: Function) => {
    dispatch(importConfirm(Entity.uploadManufacturedDevices));
    dispatch({type: IMPORT_MANUFACTURED_DEVICES, show, result});
  };
};

/**
 * Invoked when the the Manufactured Devices auditor is to be displayed or removed.
 */
export const auditManufacturedDevices = (device: ManufacturedDevice, show = true) => ({
  type : AUDIT_MANUFACTURED_DEVICES, device, show
});

export const showAuditManufacturedDevicesChangesDialog = (auditRecord: AuditChange, show = true) => ({
  type : AUDIT_MANUFACTURED_DEVICES_SHOW_CHANGES_DIALOG, auditRecord, show
});

/**
 * REDUCERS
 */
interface ManufacturedDevicesImportState {
  isImporting: boolean;
  result: ManufacturedDevice | ManufacturedDevice[] | null;
};

export interface ManufacturedDevicesAction extends AdminAction {
  searchParams?: GenericObject;
  device?: ManufacturedDevice;
  devices?: ManufacturedDevice[];
};

export interface ManufacturedDevicesActionState {
  selected: ManufacturedDevice | ManufacturedDevice[] | null;
  result: ManufacturedDevice | ManufacturedDevice[] | null;
  isEditing: boolean;
  isView?: boolean;
};

export interface ManufacturedDevicesAuditState extends ManufacturedDevicesActionState {
  showChangesDialog?: boolean;
  auditRecord?: AuditRecordType;
};

export interface ManufacturedDevicesState extends PageState {
  manufacturedDevices: {
    error: ServiceError;
    loading: boolean;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    includeSubGroups: boolean,
    pageSize: number
  },
  bulkImport: ManufacturedDevicesImportState,
  audit: ManufacturedDevicesAuditState
};

// defines the user preference key for the manufactured devices page ...
export const PREFERENCE = 'manufacturedDevices';

// defines the initial state for the reducer ...
export const initialState: ManufacturedDevicesState = {
  isInitialized       : false,
  manufacturedDevices : {
    error   : null,
    loading : false
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    includeSubGroups    : false,
    pageSize            : gridDefaultPageSize
  },
  bulkImport : {
    isImporting : false,
    result      : null
  },
  audit : {
    showDialog        : false,
    selected          : null,
    loading           : false,
    error             : null,
    result            : null,
    showChangesDialog : false,
    auditRecord       : null
  }
};

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_MANUFACTURED_DEVICES] : draft => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
  },
  [SET_GRID_COLUMNS] : ({grid, manufacturedDevices}: ManufacturedDevicesState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : manufacturedDevices.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: ManufacturedDevicesState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [SET_GRID_PAGE_SIZE] : ({grid}: ManufacturedDevicesState, {pageSize}) => {
    grid.pageSize = pageSize;
  },
  [IMPORT_MANUFACTURED_DEVICES] : ({bulkImport}: ManufacturedDevicesState, {show, result}) => {
    bulkImport.isImporting = show;
    bulkImport.result = result;
  },
  [AUDIT_MANUFACTURED_DEVICES] : ({audit}: ManufacturedDevicesState, {device, show}: ManufacturedDevicesAction) => {
    audit.selected  = show ? device : null;
    audit.isEditing = show;
    audit.result = null;
  },
  [AUDIT_MANUFACTURED_DEVICES_REQUEST] : ({audit}: ManufacturedDevicesState) => {
    audit.loading = true;
    audit.error = audit.result = null;
  },
  [AUDIT_MANUFACTURED_DEVICES_RESPONSE] : ({audit}: ManufacturedDevicesState, {result}: ManufacturedDevicesAction) => {
    audit.loading = false;
    audit.showDialog = false;
    audit.result = result;
  },
  [AUDIT_MANUFACTURED_DEVICES_ERROR] : ({audit}: ManufacturedDevicesState, {error}: ManufacturedDevicesAction) => {
    audit.loading = false;
    audit.showDialog = false;
    audit.error = error;
  },
  [AUDIT_MANUFACTURED_DEVICES_SHOW_CHANGES_DIALOG] : ({audit}: ManufacturedDevicesState, {auditRecord, show}) => {
    audit.showChangesDialog = show;
    audit.auditRecord       = auditRecord;
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: ManufacturedDevicesState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
